import { computed } from 'vue';
import { useStore } from 'vuex';

/**
 * Centralized pipeline permissions composable.
 * Use this instead of checking `currentUser.role === 'administrator'` directly in components.
 *
 * @param {Object} pipeline - Optional pipeline object with settings.agents for membership check
 */
export function usePipelinePermissions(pipeline = null) {
  const store = useStore();

  const currentUser = computed(() => store.getters.getCurrentUser);

  // Account-aware admin check (achado Codex): o papel BASE do account_user pode
  // ser 'administrator' enquanto a conta tem um custom_role_id que NÃO concede
  // admin (divergência base×custom-role que já vazou gates em prod). Admin de
  // verdade = papel administrator E sem custom role. O backend (Pundit) é o
  // guarda real; isto evita mostrar ação admin-only a quem não é admin.
  const isAdmin = computed(
    () =>
      store.getters.getCurrentRole === 'administrator' &&
      !store.getters.getCurrentCustomRoleId
  );

  /**
   * True if the current user is a member of the given pipeline (by agents list)
   * or is an administrator (admins always have access).
   */
  const isMember = computed(() => {
    if (isAdmin.value) return true;
    if (!pipeline) return false;
    // Tolerant of any unexpected shape of settings.agents (null, object,
    // string) — only a non-empty array restricts. Matches backend
    // Pipeline.visible_to_user / #assigned_agent_ids.
    const rawAgents = pipeline.value?.settings?.agents;
    const agents = Array.isArray(rawAgents) ? rawAgents : [];
    // Empty agents array means unrestricted pipeline — all agents can view it.
    if (agents.length === 0) return true;
    // Tolerant of both storage shapes: [{ id: N }] (canonical) and [N] (raw ids
    // from external API / legacy). Matches backend Pipeline.visible_to_user.
    return agents.some(agent => {
      const agentId =
        agent !== null && typeof agent === 'object' ? agent.id : agent;
      return String(agentId) === String(currentUser.value?.id);
    });
  });

  /** Can view the pipeline board and cards */
  const canViewPipeline = computed(() => isAdmin.value || isMember.value);

  /** Can create a new card in the pipeline */
  const canCreateCard = computed(() => isAdmin.value || isMember.value);

  /** Can edit (update) an existing card */
  const canEditCard = computed(() => isAdmin.value || isMember.value);

  /** Can delete a card — admin only */
  const canDeleteCard = computed(() => isAdmin.value);

  /** Can manage (create/edit/delete) pipelines and funnels — admin only */
  const canManagePipeline = computed(() => isAdmin.value);

  /** Can edit pipeline settings (webhook URL, label-stage mappings) — admin only */
  const canEditSettings = computed(() => isAdmin.value);

  /** Can create or edit pipeline automations — admin only */
  const canManageAutomations = computed(() => isAdmin.value);

  /** Can perform bulk assignment of cards — admin only */
  const canBulkAssign = computed(() => isAdmin.value);

  /** Can move a card between stages */
  const canMoveCard = computed(() => isAdmin.value || isMember.value);

  /** Can open the pipeline trash, restore and permanently delete cards — admin only */
  const canManageTrash = computed(() => isAdmin.value);

  return {
    isAdmin,
    isMember,
    canViewPipeline,
    canCreateCard,
    canEditCard,
    canDeleteCard,
    canManagePipeline,
    canEditSettings,
    canManageAutomations,
    canBulkAssign,
    canMoveCard,
    canManageTrash,
  };
}
